import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import api from '../../services/api';
import ContestantHeader from '../../components/ContestantHeader';
import PaymentForm from '../../components/PaymentForm';
import { formatCurrency } from '../../utils/currency';
import './contestant.css';

// Load Stripe outside the component (same pattern as ContestantSubmit)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ---------------------------------------------------------------------------
// ContestantPayment
// Reached via the "Pay entry fee" link on dashboard song cards that still have
// payment_status = 'pending'.  Loads all pending songs, lets the contestant
// choose which ones to pay for via checkboxes, creates a single PaymentIntent
// for the selection, then renders the shared PaymentForm.
//
// Steps:
//   loading  → load pending songs
//   none     → no pending songs (all paid)
//   select   → show checkbox list + dynamic total
//   paying   → Stripe PaymentForm
//   success  → payment confirmed
//   error    → fatal error
// ---------------------------------------------------------------------------

export default function ContestantPayment() {
  const [pageState,   setPageState]   = useState('loading');
  const [pageError,   setPageError]   = useState('');

  // ── Loaded pending songs ─────────────────────────────────────────────────
  const [pendingSongs,  setPendingSongs]  = useState([]); // [{ id, title, category_name }]
  const [feePerSong,    setFeePerSong]    = useState(0);
  const [isMember,      setIsMember]      = useState(false);

  // ── Selection state ──────────────────────────────────────────────────────
  // Set of selected song IDs (all selected by default)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Stripe / payment data ────────────────────────────────────────────────
  const [clientSecret, setClientSecret] = useState('');
  const [paymentSongIds, setPaymentSongIds] = useState([]);
  const [paymentAmount, setPaymentAmount]   = useState(0);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [intentError, setIntentError]       = useState('');

  // ── 1. Load pending songs on mount ───────────────────────────────────────
  useEffect(() => {
    async function init() {
      try {
        const cRes     = await api.get('/api/contestants/me');
        const allSongs = cRes.data.contestant?.songs ?? [];

        // Keep only active songs that haven't been paid yet
        const pending = allSongs.filter(
          (s) => s.payment_status === 'pending' && s.status !== 'withdrawn'
        );

        if (pending.length === 0) {
          setPageState('none');
          return;
        }

        // Fetch fee settings to show pricing
        const sRes = await api.get('/api/contestants/settings');
        const settings = sRes.data.settings;
        const member   = cRes.data.contestant?.is_member ?? false;
        const fee      = member
          ? settings.member_fee_cents
          : settings.non_member_fee_cents;

        const simplified = pending.map((s) => ({ id: s.id, title: s.title, category_name: s.category_name ?? '' }));
        setPendingSongs(simplified);
        setFeePerSong(fee);
        setIsMember(member);

        // Select all by default
        setSelectedIds(new Set(simplified.map((s) => s.id)));

        setPageState('select');
      } catch (err) {
        setPageError(
          err.response?.data?.error ?? 'Failed to load payment details. Please try again.'
        );
        setPageState('error');
      }
    }

    init();
  }, []);

  // ── Toggle a song checkbox ───────────────────────────────────────────────
  const toggleSong = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setIntentError('');
  };

  // ── Select / deselect all ────────────────────────────────────────────────
  const toggleAll = () => {
    if (selectedIds.size === pendingSongs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingSongs.map((s) => s.id)));
    }
    setIntentError('');
  };

  // ── Derived: total amount for selected songs ─────────────────────────────
  const selectedCount  = selectedIds.size;
  const totalAmount    = feePerSong * selectedCount;

  // ── 2. Proceed to payment: create PaymentIntent for selected songs ────────
  const handleProceedToPayment = async () => {
    if (selectedCount === 0) {
      setIntentError('Please select at least one song to pay for.');
      return;
    }
    setCreatingIntent(true);
    setIntentError('');
    try {
      const ids       = [...selectedIds];
      const intentRes = await api.post('/api/payments/create-intent', { song_ids: ids });

      const {
        client_secret,
        amount: amt,
      } = intentRes.data;

      setClientSecret(client_secret);
      setPaymentSongIds(ids);
      setPaymentAmount(amt);
      setPageState('paying');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setIntentError(
        err.response?.data?.error ?? 'Failed to set up payment. Please try again.'
      );
    } finally {
      setCreatingIntent(false);
    }
  };

  // ── 3. Payment success ───────────────────────────────────────────────────
  const handlePaymentSuccess = async (stripePaymentIntentId) => {
    try {
      await api.post('/api/payments/confirm', {
        payment_intent_id: stripePaymentIntentId,
        song_ids:          paymentSongIds,
      });
    } catch (err) {
      // Stripe webhook will also mark songs paid — non-fatal
      console.warn('Payment confirm error (webhook will handle):', err.message);
    }
    setPageState('success');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-loading">
            <div className="c-spinner" aria-label="Loading…" />
            <p>Loading payment details…</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (pageState === 'error') {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
          <div className="c-alert" role="alert">{pageError}</div>
        </main>
      </div>
    );
  }

  // ── No pending payments ──────────────────────────────────────────────────
  if (pageState === 'none') {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-empty">
            <div className="c-empty__icon">✅</div>
            <h3 className="c-empty__title">No outstanding payments</h3>
            <p className="c-empty__text">All your song entries are fully paid up.</p>
            <Link to="/contestant/dashboard" className="c-btn">
              Back to Dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────
  if (pageState === 'success') {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-success">
            <div className="c-success__icon">🎉</div>
            <h2 className="c-success__title">Payment Complete!</h2>
            <p className="c-success__text">
              Your payment of{' '}
              <strong>{formatCurrency(paymentAmount)}</strong> was successful.
              We'll be in touch with next steps.
            </p>
            <Link to="/contestant/dashboard" className="c-btn">
              View My Dashboard →
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ── Paying: show Stripe PaymentForm ─────────────────────────────────────
  if (pageState === 'paying') {
    const selectedSongs = pendingSongs.filter((s) => selectedIds.has(s.id));
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <button
            type="button"
            className="c-back-btn"
            onClick={() => setPageState('select')}
          >
            ← Back to song selection
          </button>

          <p className="c-payment-heading">Pay Entry Fee</p>
          <p className="c-payment-subheading">
            Complete payment for{' '}
            {selectedSongs.length > 1
              ? `${selectedSongs.length} selected songs`
              : 'your selected song'}.
          </p>

          <Elements stripe={stripePromise}>
            <PaymentForm
              clientSecret={clientSecret}
              songs={selectedSongs}
              amount={paymentAmount}
              feePerSong={feePerSong}
              isMember={isMember}
              onSuccess={handlePaymentSuccess}
            />
          </Elements>
        </main>
      </div>
    );
  }

  // ── Select: choose which songs to pay for ────────────────────────────────
  return (
    <div className="c-page">
      <ContestantHeader />
      <main className="c-main">

        <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>

        <p className="c-payment-heading">Pay Entry Fee</p>
        <p className="c-payment-subheading">
          Select the songs you want to pay for. All unpaid songs are selected by default.
        </p>

        {intentError && (
          <div className="c-alert" role="alert" style={{ marginBottom: '1rem' }}>
            {intentError}
          </div>
        )}

        {/* Song checkbox list */}
        <div className="c-form-card">
          <div className="c-payment-select-header">
            <span className="c-form-card__title" style={{ margin: 0, border: 'none', padding: 0 }}>
              Your unpaid songs
            </span>
            <button
              type="button"
              className="c-toggle-all-btn"
              onClick={toggleAll}
            >
              {selectedIds.size === pendingSongs.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>

          <div className="c-payment-song-list">
            {pendingSongs.map((song) => {
              const checked = selectedIds.has(song.id);
              return (
                <label
                  key={song.id}
                  className={`c-payment-song-row${checked ? ' c-payment-song-row--checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSong(song.id)}
                    className="c-payment-song-checkbox"
                  />
                  <span className="c-payment-song-title">
                    {song.title}
                    {song.category_name && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', color: '#9ca3af', fontWeight: 400 }}>
                        {song.category_name}
                      </span>
                    )}
                  </span>
                  <span className="c-payment-song-price">
                    {formatCurrency(feePerSong)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Order summary */}
        <div className="c-payment-summary">
          <div className="c-payment-summary__badge">
            {isMember ? 'SCALA Member rate' : 'Non-member rate'}
          </div>
          <div className="c-payment-summary__total">
            <span>
              {selectedCount} song{selectedCount !== 1 ? 's' : ''} ×{' '}
              {formatCurrency(feePerSong)}
            </span>
            <span>{formatCurrency(totalAmount)}</span>
          </div>
        </div>

        <button
          type="button"
          className="c-submit-btn"
          onClick={handleProceedToPayment}
          disabled={selectedCount === 0 || creatingIntent}
        >
          {creatingIntent ? (
            <>
              <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
              Setting up payment…
            </>
          ) : selectedCount === 0 ? (
            'Select at least one song'
          ) : (
            `Pay ${formatCurrency(totalAmount)} →`
          )}
        </button>

        <div style={{ marginTop: '32px' }}>
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
        </div>

      </main>
    </div>
  );
}
